import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {Observable} from 'rxjs';
import {CommonModule} from '@angular/common';
import {PushPipe} from '@ngrx/component';

import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations';
import {UiComponentsModule} from '../../../../shared/shared-module-ui/ui-components.module';
import {SharedIconsModule} from '../../../../shared-modules/shared-icons/shared-icons.module';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';

import {IEmailSettingsFormState} from './models';
import {EmailSettingsManager} from './managers';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {
  lastTestAccountLabel,
  MAIL_AUTH_MICROSOFT_365,
  MAIL_AUTH_SMTP,
  microsoftMailboxAddress,
} from '../../models/email/microsoft-mail.utils';

@Component({
  selector: 'app-email-settings',
  templateUrl: './email-settings.component.html',
  styleUrls: ['./email-settings.component.scss'],
  standalone: true,
  imports: [CommonModule, PushPipe, UiComponentsModule, SharedIconsModule],
})
export class EmailSettingsComponent implements OnInit, OnDestroy, IHasUnsavedChanges {
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  protected formState$: Observable<IEmailSettingsFormState>;
  protected readonly animation = DEFAULT_LOADER_INFINITE;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;

  constructor(protected readonly emailSettingsManager: EmailSettingsManager) {
  }

  get haveAnyEditsBeenMade(): boolean {
    return this.emailSettingsManager.isDirty;
  }

  get isSaveButtonDisabled(): boolean {
    if (this.emailSettingsManager.currentState.microsoftMailBusyLabel) {
      return true;
    }
    if (this.emailSettingsManager.currentState.isSubmitting) {
      return true;
    }
    if (this.emailSettingsManager.emailAccountChoice() === MAIL_AUTH_MICROSOFT_365) {
      return !this.microsoftMailboxConnected();
    }
    return this.emailSettingsManager.hasValidationErrors;
  }

  protected emailAccountOptions(): Record<string, ISelectOption> {
    const choice = this.emailSettingsManager.emailAccountChoice();
    return {
      smtp: {name: 'Standard', disabled: false, selected: choice === MAIL_AUTH_SMTP},
      microsoft365: {name: 'Microsoft 365', disabled: false, selected: choice === MAIL_AUTH_MICROSOFT_365},
    };
  }

  protected mailboxAddress(): string {
    return microsoftMailboxAddress(this.emailSettingsManager.currentState.selectedColleagueEmailSettings);
  }

  protected lastTestFailed(): boolean {
    return this.emailSettingsManager.currentState.selectedColleagueEmailSettings?.lastTestStatus === 'FAILED';
  }

  protected lastTestLabel(): string {
    const settings = this.emailSettingsManager.currentState.selectedColleagueEmailSettings;
    if (!settings?.lastTestAt || !settings.lastTestStatus) {
      return '';
    }
    const when = new Date(settings.lastTestAt);
    const whenLabel = Number.isNaN(when.getTime()) ? settings.lastTestAt : when.toLocaleString();
    const outcome = settings.lastTestStatus === 'SENT' ? 'Sent' : 'Failed';
    const account = lastTestAccountLabel(settings.lastTestAuthType);
    return account ? `${outcome} · ${account} · ${whenLabel}` : `${outcome} · ${whenLabel}`;
  }

  protected lastTestDetail(): string {
    const settings = this.emailSettingsManager.currentState.selectedColleagueEmailSettings;
    if (settings?.lastTestStatus !== 'FAILED') {
      return '';
    }
    return settings.lastTestMessage || '';
  }

  /**
   * True once a Microsoft mailbox is saved for the selected account.
   */
  protected microsoftMailboxConnected(): boolean {
    return this.emailSettingsManager.selectedUserOwnsMicrosoftConnection()
      && this.mailboxAddress().length > 0;
  }

  protected microsoftActionsDisabled(): boolean {
    return !this.emailSettingsManager.canUseMicrosoftAccount();
  }

  async onShowModalUnsavedChanges(): Promise<boolean> {
    return this.emailSettingsManager.tryDiscardUnsavedChanges([
      'Do you want to discard your changes and continue?',
    ]);
  }

  ngOnInit(): void {
    this.formState$ = this.emailSettingsManager.initialize();
    this.unsavedChangesService.registerComponent(this);
  }

  ngOnDestroy(): void {
    this.emailSettingsManager.teardown();
    this.unsavedChangesService.unregisterComponent();
  }

  protected getUserNameInitials(userName: string | null): string {
    return userName ? userName.charAt(0).toUpperCase() : '';
  }
}
